import { toast } from "sonner";
import { ClientHttpError } from "@/lib/http-client";

/* =====================================
   API Error Response Shape
===================================== */

export interface ApiErrorResponse {
  message?: string | string[];
  statusCode?: number;
  error?: string;

  errors?: Array<{
    field: string;
    message: string;
  }>;
}

/* =====================================
   Validation Errors (Multiple Messages)
===================================== */

export type ValidationErrors = {
  [field: string]: string[];
};

function getApiPayload(error: ClientHttpError): ApiErrorResponse | undefined {
  const data = error.response.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return data as ApiErrorResponse;
  }
  return undefined;
}

/** Map raw DB / framework messages to clearer Arabic copy where helpful. */
function humanizeKnownInfrastructureMessages(
  message: string,
  requestUrl?: string,
): string {
  const lower = message.toLowerCase();
  if (
    lower === "internal server error" ||
    lower === "authentication service error"
  ) {
    // The sign-in wording only fits the auth endpoints; anywhere else it is misleading.
    if (requestUrl && !requestUrl.includes("/auth")) {
      return "حدث خطأ في الخادم. يرجى المحاولة مرة أخرى.";
    }
    return "تعذّر إتمام تسجيل الدخول. يرجى المحاولة مرة أخرى أو إعادة إرسال الرمز.";
  }
  if (
    lower.includes("courses_instructor_id_fkey") ||
    (lower.includes("foreign key constraint") &&
      lower.includes("courses") &&
      lower.includes("instructor"))
  ) {
    return "لا يمكن حذف هذا المستخدم لأنه معيّن كمدرّس لمقررات. يُرجى إعادة تعيين المدرّسين لتلك المقررات ثم المحاولة مجدداً.";
  }
  return message;
}

/** NestJS may return `message` as a string or string[]; some APIs nest under `data`. */
function extractPrimaryMessageFromPayload(
  data: ApiErrorResponse | Record<string, unknown>,
): string | undefined {
  const raw = (data as Record<string, unknown>).message;
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  if (Array.isArray(raw)) {
    const parts = raw.filter((m): m is string => typeof m === "string");
    if (parts.length) return parts.join(". ");
  }
  // Strapi wraps errors as `{ data: null, error: { status, name, message } }`.
  // (NestJS returns `error` as a string; that case is handled by the caller.)
  const errObj = (data as Record<string, unknown>).error;
  if (errObj && typeof errObj === "object" && !Array.isArray(errObj)) {
    const m = (errObj as Record<string, unknown>).message;
    if (typeof m === "string" && m.trim()) return m.trim();
  }
  const nested = (data as Record<string, unknown>).data;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return extractPrimaryMessageFromPayload(nested as Record<string, unknown>);
  }
  return undefined;
}

function isAbortLike(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") return true;
  return error instanceof Error && error.name === "AbortError";
}

/* =====================================
   Extract Error Message
===================================== */

export function getErrorMessage(error: unknown): string {
  if (error instanceof ClientHttpError) {
    const data = getApiPayload(error);
    const primary = data
      ? extractPrimaryMessageFromPayload(data as ApiErrorResponse)
      : undefined;
    if (primary)
      return humanizeKnownInfrastructureMessages(primary, error.config.url);

    if (data?.error && typeof data.error === "string") {
      if (data.error !== "InternalServerErrorException") {
        return humanizeKnownInfrastructureMessages(
          data.error,
          error.config.url,
        );
      }
    }

    return humanizeKnownInfrastructureMessages(
      error.message || "حدث خطأ غير متوقع",
      error.config.url,
    );
  }

  if (isAbortLike(error)) {
    return "انتهت مهلة الطلب. حاول مرة أخرى.";
  }

  if (error instanceof Error) {
    if (error.message === "Network error") {
      return "فشل الاتصال بالخادم. تحقق من الإنترنت.";
    }
    return humanizeKnownInfrastructureMessages(error.message);
  }

  if (typeof error === "string") {
    return error;
  }

  return "حدث خطأ غير متوقع";
}

/* =====================================
   Extract Success Message
===================================== */

function firstNonEmptyString(
  ...candidates: (string | undefined | null)[]
): string | undefined {
  for (const c of candidates) {
    const t = typeof c === "string" ? c.trim() : "";
    if (t) return t;
  }
  return undefined;
}

/** Read `message` from wrapped API success envelopes (top-level or nested under `data`). */
export function getSuccessMessage(response: unknown): string | undefined {
  if (!response || typeof response !== "object") return undefined;

  const root = response as Record<string, unknown>;
  const topLevel = firstNonEmptyString(
    typeof root.message === "string" ? root.message : undefined,
  );
  if (topLevel) return topLevel;

  const nested = root.data;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    const inner = nested as Record<string, unknown>;
    return firstNonEmptyString(
      typeof inner.message === "string" ? inner.message : undefined,
    );
  }

  return undefined;
}

/** Show a success toast from the API response, or an optional fallback (e.g. DELETE 204). */
export function toastMutationSuccess(
  response: unknown,
  options?: { fallbackMessage?: string },
): void {
  const message = getSuccessMessage(response);
  if (message) {
    toast.success(message);
    return;
  }
  if (options?.fallbackMessage?.trim()) {
    toast.success(options.fallbackMessage.trim());
  }
}

/* =====================================
   Extract Validation Errors
===================================== */

export function getValidationErrors(error: unknown): ValidationErrors | null {
  if (!(error instanceof ClientHttpError)) return null;

  const data = getApiPayload(error);

  if (!data?.errors || !Array.isArray(data.errors)) {
    return null;
  }

  const validationErrors: ValidationErrors = {};

  data.errors.forEach(({ field, message }) => {
    if (!validationErrors[field]) {
      validationErrors[field] = [];
    }

    validationErrors[field].push(message);
  });

  return validationErrors;
}

/* =====================================
   Get Status Code
===================================== */

export function getErrorStatusCode(error: unknown): number | null {
  if (error instanceof ClientHttpError) {
    return error.response.status;
  }
  return null;
}

/* =====================================
   Status Helpers
===================================== */

export const isValidationError = (error: unknown) =>
  getErrorStatusCode(error) === 422;

export const isUnauthorizedError = (error: unknown) =>
  getErrorStatusCode(error) === 401;

export const isForbiddenError = (error: unknown) =>
  getErrorStatusCode(error) === 403;

export const isNotFoundError = (error: unknown) =>
  getErrorStatusCode(error) === 404;

export const isServerError = (error: unknown) => {
  const status = getErrorStatusCode(error);
  return status !== null && status >= 500;
};

/* =====================================
   Auth Auto Handler
===================================== */

function handleAuthError(error: unknown) {
  if (!isUnauthorizedError(error)) return;

  if (error instanceof ClientHttpError) {
    const isLoginRequest = error.config.url.includes("/api/auth/login");
    if (isLoginRequest) return;
  }

  toast.error("انتهت الجلسة. يرجى تسجيل الدخول مرة أخرى.");
}

/* =====================================
   Dev Logger
===================================== */

function logError(error: unknown) {
  if (import.meta.env.NODE_ENV === "development") {
    console.error("API Error:", error);
  }
}

/* =====================================
   Global API Error Handler
===================================== */

export function handleApiError(
  error: unknown,
  options?: {
    customMessage?: string;
    silent?: boolean;
  },
) {
  const message = options?.customMessage || getErrorMessage(error);

  /* Auth Handling */
  handleAuthError(error);

  /* Toast — لا نكرّر رسالة عامة مع رسالة انتهاء الجلسة */
  if (!options?.silent && !isUnauthorizedError(error)) {
    toast.error(message);
  }

  /* Logging */
  logError(error);
}

/* =====================================
   Form Error Handler
===================================== */

export function handleFormError(error: unknown): ValidationErrors | null {
  const validationErrors = getValidationErrors(error);

  /* Validation Errors */
  if (validationErrors) {
    const firstField = Object.keys(validationErrors)[0];

    if (firstField) {
      const firstMessage = validationErrors[firstField][0];

      toast.error(firstMessage);
    }

    logError(error);

    return validationErrors;
  }

  /* General Errors */
  handleApiError(error);

  return null;
}

/* =====================================
   React Query / TanStack Support
===================================== */

export function createQueryErrorHandler() {
  return (error: unknown) => {
    handleApiError(error);
  };
}
